import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The sign-in card's two panels, side by side, sliding between them.
 *
 * **The height follows the ACTIVE panel and is measured, not assumed.** The two panels are
 * different heights — 289px for the password, 445px for the enrolment — so a card sized to the
 * taller one would leave the password step sitting under a hand's width of empty white, on
 * every sign-in. Measuring is what lets the card be the size of what it is showing.
 *
 * It is deliberately NOT a shared component. It knows there are exactly two panels and that
 * the second is reached from the first; a general one would have to invent a direction, a
 * count and a key, none of which has a second caller to answer for.
 *
 * Motion needs no guard of its own: `index.css` collapses every transition under
 * `prefers-reduced-motion`, so this becomes an instant swap for a reader who asked for that.
 */
export function SignInCardSlide({
  showSecond,
  first,
  second,
}: {
  showSecond: boolean;
  first: ReactNode;
  second: ReactNode;
}) {
  const firstPanel = useRef<HTMLDivElement | null>(null);
  const secondPanel = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  /*
    Re-measured on every change AND on every resize of the active panel.

    A one-off measurement is wrong the moment an error banner appears inside the panel, which
    is exactly when the reader is looking at it — the card would clip the sentence explaining
    why their PIN was refused. A ResizeObserver on the live panel is what keeps the two in step
    without the panels having to tell the track anything.
  */
  useLayoutEffect(() => {
    const active = showSecond ? secondPanel.current : firstPanel.current;

    if (!active) {
      return;
    }

    const measure = () => setHeight(active.offsetHeight);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(active);

    return () => observer.disconnect();
  }, [showSecond]);

  return (
    <div
      className="relative overflow-hidden transition-[height] duration-300 ease-out"
      /*
        BOTH panels are absolute, so this box has no intrinsic height and the measurement is
        the only thing giving it one. That is why it is a LAYOUT effect: measured and applied
        before the browser paints, so the card never shows a collapsed frame on arrival and
        never animates open from zero.
      */
      style={height === null ? undefined : { height }}
    >
      {/*
        EACH PANEL CARRIES ITS OWN TRANSFORM, rather than both riding one wide track.

        A `w-[200%]` track translated by -50% is the obvious construction and it does not
        survive this card: the track's own origin is not the clipping box's left edge, so the
        second panel landed 214px outside it and rendered as a vertical sliver with its left
        third cut off. Measured, not guessed — and the fix is to stop making one element's
        position depend on another's width. Each panel is pinned to the same left edge and
        moves a whole panel width, which is a distance it can state about itself.

        The hidden panel is taken out of the tab run and off the accessibility tree too, or tab
        from the password field walks into six PIN boxes sitting off-screen and a screen reader
        announces a form the reader cannot see.
      */}
      <div
        ref={firstPanel}
        className="absolute inset-x-0 top-0 transition-transform duration-300 ease-out"
        style={{ transform: showSecond ? 'translateX(-100%)' : 'translateX(0)' }}
        aria-hidden={showSecond || undefined}
        inert={showSecond}
      >
        {first}
      </div>
      <div
        ref={secondPanel}
        className="absolute inset-x-0 top-0 transition-transform duration-300 ease-out"
        style={{ transform: showSecond ? 'translateX(0)' : 'translateX(100%)' }}
        aria-hidden={!showSecond || undefined}
        inert={!showSecond}
      >
        {second}
      </div>
    </div>
  );
}
